import { convertToMinutes, formatDateToDDMMYYYY, isTimeBefore6PM, isWeekday } from "./utils";

/**
 * Matches ServiceDropdown service pricing: strictly before 18:00 (not utils.isTimeBefore6PM which uses 18:10).
 */
// function isTimeBefore6PMForServicePrice(startTime) {
// 	if (!startTime) return false;
// 	const normalized = String(startTime).slice(0, 5);
// 	const startMinutes = convertToMinutes(normalized);
// 	const sixPM = 18 * 60 + 1;
// 	return startMinutes < sixPM;
// }

/**
 * @param {unknown} value
 * @returns {number}
 */
export function normalizePriceField(value) {
	if (value == null || value === "") return NaN;
	const n =
		typeof value === "number"
			? value
			: Number.parseFloat(String(value).replace(/,/g, ""));
	return Number.isFinite(n) ? n : NaN;
}

/**
 * Normalize DD-MM-YYYY or Date-like input to DD-MM-YYYY for isWeekday (same as ServiceDropdown).
 * @param {Date|string|number|undefined|null} input
 * @returns {string}
 */
function toDateStrForWeekday(input) {
	if (!input) return "";
	if (typeof input === "string" && /^\d{2}-\d{2}-\d{4}$/.test(input.trim())) {
		return input.trim();
	}
	const d = input instanceof Date ? input : new Date(input);
	if (Number.isNaN(d.getTime())) return "";
	return formatDateToDDMMYYYY(d);
}

/**
 * Shared branch logic for service and add-on rows that use `price_rate` (same rules as ServiceDropdown.getServicePrice).
 *
 * @param {Object|null|undefined} rate - e.g. service.price_rate or addon.price_rate
 * @param {Object|null|undefined} entity - fallback fields: price, member_price
 * @param {Object} context
 * @param {boolean} [context.isMember]
 * @param {boolean} [context.isMembershipApplied] - alias for isMember
 * @param {boolean} [context.publicHoliday]
 * @param {Date|string} [context.selectedDate] - booking / slot date
 * @param {string} [context.serviceTime] - slot start time (24h)
 * @param {{ date?: *, time?: string }} [context.selectedDateTime] - optional; overrides selectedDate + serviceTime
 * @returns {number} Resolved price as number, or NaN if nothing usable
 */
export function getEffectivePriceFromRate(rate, entity, context = {}) {
	const {
		isMember,
		isMembershipApplied,
		publicHoliday = false,
		selectedDate,
		serviceTime,
		selectedDateTime,
	} = context;

	const membershipOn = Boolean(isMember ?? isMembershipApplied);

	const dateForWeekday =
		selectedDateTime && typeof selectedDateTime === "object"
			? selectedDateTime.date ?? selectedDate
			: selectedDate;
	const timeStr =
		selectedDateTime && typeof selectedDateTime === "object"
			? selectedDateTime.time ?? serviceTime
			: serviceTime;

	const fallbackRaw = entity?.price;
	const fallbackMemberRaw = entity?.member_price;

	if (!rate || typeof rate !== "object") {
		const raw = membershipOn ? fallbackMemberRaw ?? fallbackRaw : fallbackRaw;
		return normalizePriceField(raw);
	}

	const dateStr = toDateStrForWeekday(dateForWeekday);
	const isMemberRateApplied =
		isTimeBefore6PM(timeStr) &&
		membershipOn &&
		!publicHoliday &&
		isWeekday(dateStr);

	let chosenRaw;
	if (isMemberRateApplied) {
		chosenRaw =
			rate.member_price_before_6_pm ??
			rate.memberPrice ??
			fallbackMemberRaw ??
			fallbackRaw;
	} else if (membershipOn) {
		chosenRaw =
			rate.member_after_6_or_weekends ??
			rate.memberPrice ??
			fallbackMemberRaw ??
			fallbackRaw;
	} else {
		chosenRaw = rate.nonMemberPrice ?? fallbackRaw;
	}

	let n = normalizePriceField(chosenRaw);
	if (Number.isFinite(n)) return n;

	const chain = [fallbackRaw, fallbackMemberRaw, rate.nonMemberPrice, rate.memberPrice];
	for (const c of chain) {
		n = normalizePriceField(c);
		if (Number.isFinite(n)) return n;
	}
	return NaN;
}

/**
 * @param {Object|null|undefined} addon
 * @param {Object} context - see getEffectivePriceFromRate; supports selectedDateTime: { date, time }
 * @returns {number}
 */
export function getEffectiveAddonPrice(addon, context) {
	return getEffectivePriceFromRate(addon?.price_rate, addon, context);
}
